import React, {useEffect, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {Paragraph} from "@reside/ui";
import {UserService} from "../../services/UserService";
import {Spinner} from "../../atoms/spinner";
import {dispatch} from "../../store";
import {Banner} from "../../atoms/banner";
import {ButtonLink} from "../../atoms/button-link";

/**
 * SSO login redirect handler page. Processes SSO authentication callback from backend.
 *
 * Receives token and userId from query params, sets session token, fetches user from backend, sets session user, then redirects to dashboard.
 *
 * Called after backend sends HTML redirect response to SSO provider containing this page's URL with auth credentials.
 */

export const SSOLoginRedirectPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const processLogin = async () => {
      try {
        // Parse query parameters
        const searchParams = new URLSearchParams(location.search);

        const token = searchParams.get("token");
        const userId = searchParams.get("userId");

        if (!token || !userId) {
          setError("No authentication token or username provided");
          setLoading(false);
          return;
        }

        await dispatch.adminSession.setToken({token, isLoginToken: true});

        const response = await UserService.find(userId);

        await dispatch.adminSession.setUser({
          user: response.data,
          isUserLogin: true,
        });

        // Redirect to dashboard after successful login
        navigate("/admin/dashboard");
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Authentication failed. Please try again.",
        );
        setLoading(false);
      }
    };

    processLogin();
  }, [location.search, navigate]);

  return loading ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
      }}
    >
      <Spinner /> {/* Loading spinner centered  */}
      <Paragraph>Authenticating...</Paragraph>
      <Paragraph fontSize="1.25em" lineHeight="1.75em">
        Please wait while we log you in.
      </Paragraph>
    </div>
  ) : (
    <Banner title={error || "Authentication Error"}>
      <ButtonLink to="/admin">Return to Login</ButtonLink>
    </Banner>
  );
};
